import { useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { ArrowBack } from "@mui/icons-material";

interface PaymentIframeProps {
  paymentLink: string;
  onBack: () => void;
  showBackButton?: boolean;
}

/** Referência “folgada” do checkout MP (um pouco menor que 1280 para reduzir rolagem horizontal). */
const FIT_WIDTH_REF = 1240;
/** Altura de referência de uma tela típica do fluxo (não é a página inteira; só ajuda o cálculo). */
const FIT_HEIGHT_REF = 880;

/**
 * Ajuste leve sobre o encaixe ideal: ~1,5% menor, para ganhar um pouco de margem sem afastar o conteúdo.
 * Piso 0,9 evita voltar ao zoom forte que o usuário rejeitou.
 */
function computeSubtleZoom(containerWidth: number, containerHeight: number): number {
  if (containerWidth <= 0 || containerHeight <= 0) return 1;
  const fit = Math.min(
    containerWidth / FIT_WIDTH_REF,
    containerHeight / FIT_HEIGHT_REF,
    1,
  );
  const withMargin = fit * 0.985;
  return Math.min(1, Math.max(0.9, withMargin));
}

/**
 * Componente que exibe o iframe de pagamento do Mercado Pago
 * @param paymentLink URL do Mercado Pago para pagamento
 * @param onBack Função chamada quando o usuário clica no botão voltar
 */
export function PaymentIframe({
  paymentLink,
  onBack,
  showBackButton = true,
}: PaymentIframeProps) {
  /** Fluxo em página inteira: botão voltar sobre o iframe. */
  if (showBackButton) {
    return (
      <Stack width="100%" height="100%" position="relative">
        <IconButton
          onClick={onBack}
          sx={{
            p: 1,
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 1,
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            "&:hover": {
              bgcolor: "background.paper",
              border: "1px solid rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          <ArrowBack fontSize="large" />
        </IconButton>
        <iframe
          src={paymentLink}
          width="100%"
          height="100%"
          style={{ border: "none" }}
          title="Pagamento Mercado Pago"
        />
      </Stack>
    );
  }

  return <PaymentIframeModalLayout paymentLink={paymentLink} onBack={onBack} />;
}

/**
 * Modal: iframe preenche a área + um zoom leve (ResizeObserver) para reduzir um pouco
 * as barras de rolagem internas, sem encolher demais o conteúdo.
 */
function PaymentIframeModalLayout({
  paymentLink,
  onBack,
}: {
  paymentLink: string;
  onBack: () => void;
}) {
  const areaRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);

  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el) return;

    const run = () => {
      const { width, height } = el.getBoundingClientRect();
      setZoom(computeSubtleZoom(width, height));
    };

    run();
    const ro = new ResizeObserver(run);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Stack
      width="100%"
      height="100%"
      maxWidth="100%"
      direction="column"
      sx={{
        minHeight: 0,
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
        overscrollBehavior: "contain",
      }}
    >
      <Box
        ref={areaRef}
        sx={{
          flex: "1 1 0%",
          minHeight: 0,
          minWidth: 0,
          maxWidth: "100%",
          position: "relative",
          overflow: "hidden",
          bgcolor: "grey.50",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zoom,
          }}
        >
          <iframe
            src={paymentLink}
            title="Pagamento Mercado Pago"
            style={{
              border: "none",
              display: "block",
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              maxWidth: "100%",
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          maxWidth: "100%",
          overflowX: "hidden",
          bgcolor: "background.paper",
          borderTop: "1px solid rgba(0, 0, 0, 0.12)",
          p: 2,
          textAlign: "center",
          boxSizing: "border-box",
        }}
      >
        <Stack
          direction="row"
          flexWrap="wrap"
          spacing={1}
          useFlexGap
          justifyContent="center"
          alignItems="center"
          mb={1}
          sx={{ maxWidth: "100%", px: 0.5 }}
        >
          <CheckCircle color="success" sx={{ flexShrink: 0 }} />
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              textAlign: "center",
              wordBreak: "break-word",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            Após finalizar o pagamento, clique abaixo para prosseguir
          </Typography>
        </Stack>
        <Button
          variant="contained"
          color="primary"
          onClick={onBack}
          sx={{ minWidth: 200, maxWidth: "100%" }}
        >
          Prosseguir
        </Button>
      </Box>
    </Stack>
  );
}
